import React, { useState, useEffect, useCallback } from "react";
import FlagIcon from "@mui/icons-material/Flag";
import {
  Container,
  Grid,
  Typography,
  Button,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Popover,
  Switch,
  Paper,
  Pagination,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  EditOutlined as EditOutlinedIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const columns = [
  "bo_id",
  "user_id",
  "username",
  "email",
  "password_hash",
  "full_name",
  "phone_number",
  "role_id",
  ...Array.from({ length: 9 }, (_, i) => `reference_id_${i + 1}`),
  "reference_no_1",
  "reference_no_2",
  "reference_no_3",
  "reference_date_1",
  "reference_date_2",
  "reference_date_3",
  "delete_flag",
  "data_source",
  "created_by",
  "created_on_date",
  "created_at",
  "modified_by",
  "modified_on_date",
  "modified_at",
  "remarks",
];

const columnLabels = {
  bo_id: "Organization Id",
  user_id: "User Id",
  username: "Username",
  email: "Email",
  password_hash: "Password",
  full_name: "Full Name",
  phone_number: "Phone Number",
  role_id: "Role Id",
  data_source: "Data Source",
  remarks: "Remarks",
  ...Object.fromEntries(
    columns
      .filter(
        (col) =>
          ![
            "bo_id",
            "user_id",
            "username",
            "email",
            "password_hash",
            "full_name",
            "phone_number",
            "role_id",
            "data_source",
            "remarks",
          ].includes(col)
      )
      .map((col) => [
        col,
        col
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
      ])
  ),
};

const systemFields = [
  "delete_flag",
  "created_by",
  "created_on_date",
  "created_at",
  "modified_by",
  "modified_on_date",
  "modified_at",
];

function UserRoles() {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const [data, setData] = useState([]);
  const formFields = [
    "bo_id",
    "user_id",
    "username",
    "email",
    "password_hash",
    "full_name",
    "phone_number",
    "role_id",
    "data_source",
    "remarks",
  ];
  const [visibleCols, setVisibleCols] = useState({
    user_id: true,
    username: true,
    email: true,
    role_id: true,
    full_name: true,
    phone_number: true,
    ...Object.fromEntries(
      columns
        .filter(
          (c) =>
            ![
              "user_id",
              "username",
              "email",
              "role_id",
              "full_name",
              "phone_number",
            ].includes(c)
        )
        .map((c) => [c, false])
    ),
  });
  const [filters, setFilters] = useState({});
  const [filterAnchors, setFilterAnchors] = useState({});
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [anchorSettings, setAnchorSettings] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const query = new URLSearchParams({
        page,
        limit: rowsPerPage,
        ...filters,
      }).toString();
      const res = await fetch(
        `${BASE_URL}/api/userroles/paginated?${query}`
      );
      const json = await res.json();
      setData(json.data || []);
      setTotalPages(Math.ceil(json.total / json.limit));
    } catch (err) {
      console.error("Error fetching user roles:", err);
      setAlert({ type: "error", msg: "Failed to load user roles" });
    }
  }, [page, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = () => {
    setFormData({});
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleEdit = (item) => {
    setFormData(item);
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Confirm delete?")) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/userroles/${item.user_id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error("Delete failed");
      await fetchData();
      setAlert({ type: "success", msg: "User role deleted" });
    } catch (err) {
      setAlert({ type: "error", msg: "Failed to delete" });
    }
  };

  const handleToggleFlag = async (item) => {
    const updatedFlag = item.delete_flag === "Y" ? "N" : "Y";
    try {
      const res = await fetch(
        `${BASE_URL}/api/userroles/${item.user_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...item, delete_flag: updatedFlag }),
        }
      );
      if (!res.ok) throw new Error("Toggle failed");
      await fetchData();
      setAlert({ type: "success", msg: "Delete flag updated!" });
    } catch (err) {
      setAlert({ type: "error", msg: "Failed to update delete flag" });
    }
  };

  const handleSave = async () => {
    try {
      const method = isEditing ? "PUT" : "POST";
      const url = isEditing
        ? `${BASE_URL}/api/userroles/${formData.user_id}`
        : `${BASE_URL}/api/userroles`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Save failed");
      await fetchData();
      setOpenDialog(false);
      setAlert({ type: "success", msg: isEditing ? "Updated!" : "Added!" });
    } catch (err) {
      setAlert({ type: "error", msg: "Save failed" });
    }
  };

  return (
    <ThemeProvider theme={Table_theme}>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h5"
          gutterBottom
          color="#454545"
          fontSize="20px"
          fontWeight="600"
        >
          User Roles
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={(e) => setAnchorSettings(e.currentTarget)}>
            <SettingsIcon />
          </IconButton>
          <Popover
            open={Boolean(anchorSettings)}
            anchorEl={anchorSettings}
            onClose={() => setAnchorSettings(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box p={2} maxHeight="300px" overflow="auto">
              <Typography variant="subtitle1" mb={1}>
                Toggle Columns
              </Typography>
              {columns.map((col) => (
                <Box
                  key={col}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="body2">{columnLabels[col]}</Typography>
                  <Switch
                    size="small"
                    checked={visibleCols[col]}
                    onChange={(e) =>
                      setVisibleCols((prev) => ({
                        ...prev,
                        [col]: e.target.checked,
                      }))
                    }
                  />
                </Box>
              ))}
            </Box>
          </Popover>
          <Button variant="contained" onClick={handleAdd}>
            Add +
          </Button>
          <TextField
            size="small"
            placeholder="Search…"
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, search: e.target.value }));
              setPage(1);
            }}
          />
        </Box>

        {alert.msg && <Alert severity={alert.type}>{alert.msg}</Alert>}

        <Paper>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Actions</TableCell>
                  {columns.map(
                    (col) =>
                      visibleCols[col] && (
                        <TableCell key={col}>
                          <Box display="flex" alignItems="center">
                            {columnLabels[col]}
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                setFilterAnchors({
                                  ...filterAnchors,
                                  [col]: e.currentTarget,
                                })
                              }
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={!!filterAnchors[col]}
                              anchorEl={filterAnchors[col]}
                              onClose={() =>
                                setFilterAnchors({
                                  ...filterAnchors,
                                  [col]: null,
                                })
                              }
                            >
                              <Box p={2}>
                                <TextField
                                  size="small"
                                  placeholder={`Filter ${columnLabels[col]}`}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      [col]: e.target.value,
                                    })
                                  }
                                />
                                <Box mt={1} display="flex" gap={1}>
                                  <Button variant="contained" size="small">
                                    Apply
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                      const newFilters = { ...filters };
                                      delete newFilters[col];
                                      setFilters(newFilters);
                                    }}
                                  >
                                    Clear
                                  </Button>
                                </Box>
                              </Box>
                            </Popover>
                          </Box>
                        </TableCell>
                      )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item) => (
                  <TableRow key={item.user_id}>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(item)}>
                        <EditOutlinedIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(item)}
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleToggleFlag(item)}
                        title="Toggle Delete Flag"
                      >
                        <FlagIcon
                          style={{
                            color: item.delete_flag === "N" ? "green" : "red",
                          }}
                        />
                      </IconButton>
                    </TableCell>
                    {columns.map(
                      (col) =>
                        visibleCols[col] && (
                          // <TableCell key={col}>{item[col]}</TableCell>
                          <TableCell key={col}>
                            {col === "password_hash"
                              ? "•".repeat(item[col]?.length || 8)
                              : item[col]}
                          </TableCell>
                        )
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
            color="primary"
            variant="outlined"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "8px",
                minWidth: "32px",
                height: "32px",
                fontSize: "14px",
              },
              "& .Mui-selected": {
                backgroundColor: "#b3dbea !important",
                color: "black",
                fontWeight: "bold",
              },
              "& .MuiPaginationItem-ellipsis": {
                display: "none",
              },
            }}
          />
        </Box>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {isEditing ? "Edit User Role" : "Add User Role"}
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {formFields.map((col, idx) => (
                <Grid item xs={8} sm={2} key={idx}>
                  <TextField
                    key={col}
                    label={columnLabels[col]}
                    name={col}
                    fullWidth
                    margin="normal"
                    size="small"
                    type={col === "password_hash" ? "password" : "text"}
                    value={formData[col] || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, [col]: e.target.value })
                    }
                    InputLabelProps={{
                      sx: {
                        fontSize: "13px",
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {isEditing ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default UserRoles;
